import Loader from '@components/Loader/Loader';
import SourceAccordion from '@components/SourceAccordion/SourceAccordion';
import VerificationItemSkeleton from '@components/sentinel/skeleton/VerificationItemSkeleton/VerificationItemSkeleton';
import VideoDetectedClaimsSkeleton from '@components/sentinel/skeleton/VideoDetectedClaimsSkeleton/VideoDetectedClaimsSkeleton';
import useCurrentLang from '@hooks/useCurrentLang';
import { useStreamVerify } from '@hooks/useStreamVerify';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Markdown from 'react-markdown';
import { useSearchParams } from 'react-router';
import { CONCLUSION_ICON_MAP, CONCLUSION_LOCALE_MAP, VERIFICATION_REPORT_URL } from 'src/constants/sentinel';
import { useAppSelector } from 'universal-scripts';
import ErrorStep from '../ErrorStep/ErrorStep';
import VerificationAIImage from './VerificationAIImage';

import './VerificationStep.css';

export default function VerificationStep() {
	const detectStatus = useAppSelector((state) => state.sentinel.phases.detect.status);
	const selectedClaims = useAppSelector((state) => state.sentinel.phases.detect.selectedClaims);
	const lang = useCurrentLang();
	const verifyStatus = useAppSelector((state) => state.sentinel.phases.verify.status);
	const verificationsObject = useAppSelector((state) => state.sentinel.phases.verify.verifications);
	const refreshCache = useAppSelector((state) => state.sentinel.refreshCache);
	const [searchParams] = useSearchParams();

	const deepFakeDetection = searchParams.get('deepFakeDetection');
	const isDetectComplete = detectStatus === 'completed';
	const hasClaimsToVerify = selectedClaims.length > 0;
	const isReadyToVerify = isDetectComplete && hasClaimsToVerify;

	useStreamVerify(isReadyToVerify ? selectedClaims : null, lang, refreshCache);

	const verificationsList = useMemo(() => {
		return selectedClaims.map((claim) => {
			return verificationsObject[claim.id] || { ...claim, id: claim.id };
		});
	}, [selectedClaims, verificationsObject]);

	const isRunning = verifyStatus === 'processing';

	if (!isDetectComplete) return null;
	if (verifyStatus === 'error') return <ErrorStep />;
	if (verifyStatus === 'idle' && hasClaimsToVerify) return <Loader />;

	const isSkipped = !hasClaimsToVerify && !deepFakeDetection;
	const isCompleted = verifyStatus === 'completed';

	const stepClasses = ['sentinel-step', isCompleted ? 'sentinel-step--completed' : '', isSkipped ? 'sentinel-step--skipped' : '']
		.filter(Boolean)
		.join(' ');

	const showInitialLoading = isRunning && verificationsList.length === 0;

	return (
		<div id="verification-step" className={stepClasses}>
			<h2>
				<span className="icon icon-verify" />
				<span>
					<FormattedMessage id="sentinel.verification.title" />
				</span>
			</h2>

			{deepFakeDetection && <VerificationAIImage />}

			<div className="sentinel-note" id="verification-analysis">
				{!hasClaimsToVerify && (
					<p>
						<FormattedMessage id="sentinel.verification.no-claims" />
					</p>
				)}

				{hasClaimsToVerify && showInitialLoading && <VideoDetectedClaimsSkeleton />}

				{hasClaimsToVerify && !showInitialLoading && (
					<ul id="verification-results">
						{verificationsList.map((v) => {
							const claimId = v.id;
							const displayNumber = claimId + 1;
							if (!v.conclusion && isRunning) {
								return <VerificationItemSkeleton key={`skeleton-${claimId}`} index={displayNumber} />;
							}
							if (!v.conclusion && isCompleted) return null;
							const rawConclusion = v.conclusion || '';
							const conclusionKey = rawConclusion.toLowerCase();
							const iconClass = CONCLUSION_ICON_MAP[v.conclusion as keyof typeof CONCLUSION_ICON_MAP] || 'icon-question';
							const allSources = [...(v.factCheckSources || []), ...(v.newsArticles || [])];

							return (
								<li key={`verification-${claimId}`} className="verification-item">
									<h3 title={v.claim}>
										<span className="verification-left">
											<strong className="index">{displayNumber}</strong>
											<span>{v.claim}</span>
										</span>

										<div className="verification-badges">
											{rawConclusion && (
												<span title={conclusionKey} className={`${conclusionKey} badge`}>
													<span className={`icon ${iconClass}`} />
													<strong>
														<FormattedMessage
															id={`sentinel.verification.${CONCLUSION_LOCALE_MAP[conclusionKey]}`}
															defaultMessage={rawConclusion}
														/>
													</strong>
												</span>
											)}
											{rawConclusion && (
												<a
													href={`${VERIFICATION_REPORT_URL}?claim=${v.claim}&lang=${lang}`}
													className="button report-link"
													target="_blank"
													rel="noopener noreferrer"
												>
													<span>
														<FormattedMessage id="sentinel.report" />
													</span>
												</a>
											)}
										</div>
									</h3>
									<div className="reasoning">
										<Markdown>{v.reasoning}</Markdown>
									</div>
									{allSources.length > 0 && <SourceAccordion allSources={allSources} />}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</div>
	);
}
